import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { deleteDkimKey, listDkimKeys, verifyDkimKey } from 'src/actions/dkimKeys';
import { showAlert } from 'src/actions/globalAlert';
import { ActionPopover, DeleteModal, Loading } from 'src/components';
import { ExternalLink, PageLink, SubduedLink } from 'src/components/links';
// prettier-ignore
import { Banner, Box, Layout, Panel, ScreenReaderOnly, Stack, Table, Tag, Text } from 'src/components/matchbox';
import { SubduedText } from 'src/components/text';
import useModal from 'src/hooks/useModal';
import { DETAILS_BASE_URL } from '../constants';

const DKIM_DOCS_URL =
  'https://developers.sparkpost.com/api/sending-domains/#sending-domains-dkim-keys';

function DkimKeysTab({
  domainId,
  domain,
  dkimKeys,
  listLoading,
  listError,
  deleteLoading,
  listDkimKeys,
  verifyDkimKey,
  deleteDkimKey,
  showAlert
}) {
  const subaccountId = domain?.subaccount_id;
  const { closeModal, isModalOpen, openModal, meta: modalMeta } = useModal();

  useEffect(() => {
    if (domainId) {
      listDkimKeys({ domain: domainId, subaccount: subaccountId });
    }
  }, [listDkimKeys, domainId, subaccountId]);

  if (!domainId) {
    return <Loading />;
  }

  const handleVerify = (keyId) => {
    verifyDkimKey({ domain: domainId, keyId, subaccount: subaccountId }).then((result) => {
      if (result.dkim_status === 'valid') {
        showAlert({
          type: 'success',
          message: `DKIM key "${keyId}" has been verified successfully.`
        });
        listDkimKeys({ domain: domainId, subaccount: subaccountId });
      } else if (result.dkim_status === 'pending') {
        showAlert({
          type: 'info',
          message: `DKIM key "${keyId}" verification is pending. DNS changes may take time to propagate.`
        });
        listDkimKeys({ domain: domainId, subaccount: subaccountId });
      } else {
        showAlert({
          type: 'error',
          message: `Unable to verify DKIM key "${keyId}".`,
          details: result.dns?.dkim_error
        });
      }
    });
  };

  const handleDelete = () => {
    const { keyId } = modalMeta;
    deleteDkimKey({ domain: domainId, keyId, subaccount: subaccountId })
      .then(() => {
        closeModal();
        showAlert({
          type: 'success',
          message: `DKIM key "${keyId}" has been deleted.`
        });
        listDkimKeys({ domain: domainId, subaccount: subaccountId });
      })
      .catch(() => {
        closeModal();
      });
  };

  const getActionsForKey = (key) => {
    const actions = [];

    actions.push({
      content: 'Edit',
      to: `${DETAILS_BASE_URL}/sending-bounce/${domainId}/dkim-keys/${key.id}/edit`,
      as: PageLink
    });

    if (key.dkim_status !== 'valid') {
      actions.push({
        content: 'Verify',
        onClick: () => handleVerify(key.id)
      });
    }

    if (!key.is_default) {
      actions.push({
        content: 'Delete',
        onClick: () => openModal({ keyId: key.id })
      });
    }

    return actions;
  };

  if (listLoading) {
    return <Loading />;
  }

  if (listError) {
    const errorDetails =
      listError.response?.data?.errors?.[0]?.description ||
      listError.response?.data?.errors?.[0]?.message ||
      listError.message ||
      'An error occurred while loading DKIM keys.';

    return (
      <Banner status="danger" title="Unable to load DKIM keys">
        <p>{errorDetails}</p>
      </Banner>
    );
  }

  return (
    <>
      <Layout>
        <Layout.Section annotated>
          <Layout.SectionTitle>DKIM Keys</Layout.SectionTitle>

          <Stack>
            <SubduedText fontSize="200">
              Manage multiple DKIM signing keys for this domain. Each key can be used to sign
              outgoing messages by specifying the key ID in the transmission options.
            </SubduedText>

            <SubduedText fontSize="200">
              To rotate keys, add a new key, verify it with your DNS provider, then set it as the
              default. The old key can remain valid during the transition period.
            </SubduedText>

            <SubduedLink as={ExternalLink} to={DKIM_DOCS_URL} fontSize="200">
              DKIM Keys Documentation
            </SubduedLink>
          </Stack>
        </Layout.Section>

        <Layout.Section>
          <Panel>
            <Table>
              <thead>
                <Table.Row header>
                  <Table.HeaderCell width="15%">ID</Table.HeaderCell>
                  <Table.HeaderCell width="12%">Selector</Table.HeaderCell>
                  <Table.HeaderCell width="20%">Signing Domain</Table.HeaderCell>
                  <Table.HeaderCell width="22%">Headers</Table.HeaderCell>
                  <Table.HeaderCell width="12%">Status</Table.HeaderCell>
                  <Table.HeaderCell width="10%">Default</Table.HeaderCell>
                  <Table.HeaderCell width="32px">
                    <ScreenReaderOnly>Actions</ScreenReaderOnly>
                  </Table.HeaderCell>
                </Table.Row>
              </thead>
              <tbody>
                {dkimKeys.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={7}>
                      <Box padding="400" textAlign="center">
                        <Text>No DKIM keys found for this domain.</Text>
                      </Box>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  dkimKeys.map((key) => (
                    <Table.Row key={key.id}>
                      <Table.Cell>
                        <PageLink
                          to={`${DETAILS_BASE_URL}/sending-bounce/${domainId}/dkim-keys/${key.id}`}
                        >
                          {key.id}
                        </PageLink>
                      </Table.Cell>
                      <Table.Cell>{key.selector}</Table.Cell>
                      <Table.Cell>{key.signing_domain}</Table.Cell>
                      <Table.Cell>{key.headers}</Table.Cell>
                      <Table.Cell>
                        <StatusTag status={key.dkim_status} />
                      </Table.Cell>
                      <Table.Cell>{key.is_default ? 'Yes' : 'No'}</Table.Cell>
                      <Table.Cell>
                        {getActionsForKey(key).length > 0 && (
                          <ActionPopover actions={getActionsForKey(key)} />
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </tbody>
            </Table>
          </Panel>
        </Layout.Section>
      </Layout>

      <DeleteModal
        open={isModalOpen}
        title="Delete DKIM Key"
        content={
          <p>
            Are you sure you want to delete the DKIM key &quot;{modalMeta?.keyId}&quot;? This action
            cannot be undone. Any transmissions specifically targeting this key ID will be rejected.
          </p>
        }
        onCancel={closeModal}
        onDelete={handleDelete}
        isPending={deleteLoading}
      />
    </>
  );
}

function StatusTag({ status }) {
  switch (status) {
    case 'valid':
      return <Tag color="green">Valid</Tag>;
    case 'invalid':
      return <Tag color="red">Invalid</Tag>;
    case 'unverified':
      return <Tag color="red">Unverified</Tag>;
    case 'pending':
      return <Tag>Pending</Tag>;
    default:
      return <Tag>{status}</Tag>;
  }
}

const mapStateToProps = (state) => ({
  dkimKeys: state.dkimKeys.list || [],
  listLoading: state.dkimKeys.listLoading,
  listError: state.dkimKeys.listError,
  deleteLoading: state.dkimKeys.deleteLoading
});

export default connect(mapStateToProps, {
  listDkimKeys,
  verifyDkimKey,
  deleteDkimKey,
  showAlert
})(DkimKeysTab);
